import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useWindowsRelease } from "@/hooks/useWindowsRelease";
import { consumerErrorMessage } from "@/lib/consumerCommerce";
import { downloadWindowsRelease, startWindowsCheckout } from "@/lib/windowsReleases";

export function useDownloadPlatform() {
  const [params, setParams] = useSearchParams();
  const platform = params.get("platform") === "windows" ? "windows" : "macos";
  const choose = (value) => setParams((current) => {
    const next = new URLSearchParams(current);
    next.set("platform", value === "windows" ? "windows" : "macos");
    return next;
  }, { replace: true });
  return [platform, choose];
}

export function DownloadPlatformPicker({ productName, platform, onChange }) {
  return <label className="block text-sm text-ink-700">
    <span className="mr-3 font-semibold">Download for</span>
    <select className="border border-ink-200 bg-white px-3 py-2" aria-label={`${productName} operating system`}
      value={platform} onChange={(event) => onChange(event.target.value)}>
      <option value="macos">Mac</option><option value="windows">Windows</option>
    </select>
  </label>;
}

export default function WindowsReleaseActions({ product, owned, haloSubscriber = false, haloEntitlement, catalog, savingPending = false }) {
  const { loading, release, available } = useWindowsRelease(product, owned);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (loading) return <p role="status" className="text-sm text-ink-500">Checking Windows availability…</p>;
  if (!available) return <p role="status" className="text-sm text-ink-600">The Windows edition is coming soon.{owned ? " Your existing licence is unchanged." : ""}</p>;
  if (!owned && savingPending) return <p role="status" className="text-sm text-ink-700">Your Halo saving is being prepared. Please check back before buying.</p>;
  const act = async () => {
    setBusy(true); setError("");
    try {
      const result = owned ? await downloadWindowsRelease(product)
        : await startWindowsCheckout(product, { haloSubscriber, haloEntitlement, catalog });
      window.location.assign(owned ? result.download_url : result.url);
    } catch (err) { setError(consumerErrorMessage(err, "This Windows request could not be completed. Please try again.")); }
    finally { setBusy(false); }
  };
  return <div className="space-y-3" data-testid={`${product}-windows-actions`}>
    <p className="text-sm text-ink-600">Windows 10/11 · x64{release ? ` · Version ${release.version}` : ""}</p>
    <button className="lf-btn-primary" type="button" disabled={busy} onClick={act}>
      {busy ? "Preparing…" : owned ? "Download for Windows" : "Buy for Windows"}
    </button>
    <p className="text-xs text-ink-500">{owned ? "Use your existing website licence and the matching code in the Windows app." : "One-time website licence. The account shows your download after payment is confirmed."}</p>
    {error ? <p role="alert" className="text-sm text-red-600">{error}</p> : null}
  </div>;
}
